import { useEffect, useState } from 'react'

import { populationsApi, AudienceSummary } from '@/lib/api'

/**
 * Ready-to-select audiences for an `audience`-type template input (Step 6.4
 * follow-up: the schedule card collecting the template's declared inputs).
 * Fetched only when `enabled`, since most templates never declare an
 * `audience` input and the card should not pay for the list otherwise.
 *
 * "Ready" mirrors the same filter the compose picker applies
 * (`ConversationsPage/ComposeModal/useComposeModal.ts`, `FeaturedTwinModal.tsx`):
 * an audience mid Argo build or in an error state cannot back a quick-study
 * panel, so it is not offered as a value here either.
 */
export interface UseWorkflowScheduleAudiencesResult {
  audiences: Array<AudienceSummary>
  isLoading: boolean
}

export function useWorkflowScheduleAudiences(
  enabled: boolean,
): UseWorkflowScheduleAudiencesResult {
  const [audiences, setAudiences] = useState<Array<AudienceSummary>>([])
  const [isLoading, setIsLoading] = useState(enabled)

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    populationsApi
      .list({ limit: 500 })
      .then((result) => {
        if (cancelled) return
        setAudiences(result.filter((audience) => audience.status === 'ready'))
      })
      .catch(() => {
        if (!cancelled) setAudiences([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled])

  return { audiences, isLoading }
}
